/**
 * 端末内アカウント。
 *
 * 1台のスマホを家族で使い分けられるように、ケアの記録をユーザーごとに分ける。
 * アカウントはこの端末のIndexedDBの中だけにあり、サーバには作らない。
 *
 * ⚠️ 設計書§11.2は「アカウント登録なし（匿名UUID）」を子どもへの配慮として挙げている。
 *    ここで足すのは**端末内の仕切り**であって、サーバ上の会員登録ではない。氏名・
 *    メールアドレス・年齢は求めず、入力されたユーザー名も端末から出さない。
 *
 * ⚠️ パスワードの強さの限界を隠さないこと。ハッシュ（PBKDF2-SHA256）は端末内に
 *    あるので、端末を解析されれば総当たりはできる。守れるのは「家族どうしで
 *    記録を勝手に見ない」程度で、盗まれた端末の中身は守れない。UIにも明記する。
 */

/**
 * 鍵導出の反復回数。端末で1秒以内に収まる範囲で、できるだけ多くしている。
 * 増やすと安全側だが、古い端末でログインが目に見えて遅くなる。
 */
export const PBKDF2_ITERATIONS = 210_000;

export const MIN_PASSWORD_LENGTH = 8;
export const MAX_PASSWORD_LENGTH = 128;
export const MAX_USER_NAME_LENGTH = 20;

export const ACCOUNT_LOCAL_NOTE =
  "アカウントはこの端末の中だけにあります。ほかの端末とは共有されず、パスワードを忘れても取り出せません。";
export const ACCOUNT_SCOPE_NOTE =
  "ケアの記録と肌年齢の測定値は、ログイン中のユーザーにだけたまります。";
export const ACCOUNT_SECURITY_NOTE =
  "パスワードは、家族どうしで記録を見せないための仕切りです。端末そのものを調べられた場合は守れません。";

/** 1ユーザー。パスワードそのものは保存しない（塩とハッシュだけ） */
export interface UserAccount {
  id: string;
  /** 表示・入力に使う名前 */
  name: string;
  /** 照合用に正規化した名前（大文字小文字・空白の違いを吸収する） */
  nameKey: string;
  /** base64 */
  salt: string;
  /** base64（PBKDF2-SHA256の出力32バイト） */
  hash: string;
  iterations: number;
  createdAt: number;
}

export type Validation = { ok: true; value: string } | { ok: false; reason: string };

function subtle(): SubtleCrypto {
  // globalThis.crypto ではなく素の crypto を見る（DOM/Workerどちらの型定義でも解決できる）
  const c: Crypto | undefined = crypto;
  if (!c?.subtle) {
    // HTTPSでない場所で開くとここに来る（WebCryptoはsecure contextのみ）
    throw new Error("この環境では暗号機能が使えません（HTTPSで開いてください）");
  }
  return c.subtle;
}

function toBase64(buf: ArrayBuffer): string {
  const bytes = new Uint8Array(buf);
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

function fromBase64(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/**
 * ユーザー名の正規化。
 * 全角/半角と前後の空白の違いで「別人」にならないようにそろえる。
 */
export function normalizeUserName(raw: string): string {
  return raw.normalize("NFKC").replace(/\s+/g, " ").trim();
}

/** 照合キー。表示名は入力どおり残し、同一判定だけこちらで行う */
export function userNameKey(raw: string): string {
  return normalizeUserName(raw).toLowerCase();
}

export function validateUserName(raw: string, existing: UserAccount[] = []): Validation {
  const name = normalizeUserName(raw);
  if (name.length === 0) return { ok: false, reason: "ユーザー名を入れてください" };
  if (name.length > MAX_USER_NAME_LENGTH) {
    return { ok: false, reason: `ユーザー名は${MAX_USER_NAME_LENGTH}文字までです` };
  }
  // 記録の見出しに出るだけなので文字種は広く許すが、制御文字だけは弾く
  if (/[\u0000-\u001f\u007f]/.test(name)) {
    return { ok: false, reason: "使えない文字が入っています" };
  }
  const key = userNameKey(name);
  if (existing.some((u) => u.nameKey === key)) {
    return { ok: false, reason: "その名前はもう使われています" };
  }
  return { ok: true, value: name };
}

export function validatePassword(raw: string): Validation {
  if (raw.length < MIN_PASSWORD_LENGTH) {
    return { ok: false, reason: `パスワードは${MIN_PASSWORD_LENGTH}文字以上にしてください` };
  }
  if (raw.length > MAX_PASSWORD_LENGTH) {
    return { ok: false, reason: `パスワードは${MAX_PASSWORD_LENGTH}文字までです` };
  }
  if (/^\s|\s$/.test(raw)) {
    // 前後の空白は入力ミスのことが多く、あとで入れ直せなくなる
    return { ok: false, reason: "パスワードの前後に空白は使えません" };
  }
  return { ok: true, value: raw };
}

async function derive(password: string, salt: Uint8Array, iterations: number): Promise<string> {
  const key = await subtle().importKey(
    "raw",
    new TextEncoder().encode(password.normalize("NFKC")),
    "PBKDF2",
    false,
    ["deriveBits"],
  );
  const bits = await subtle().deriveBits(
    { name: "PBKDF2", salt: salt as unknown as BufferSource, iterations, hash: "SHA-256" },
    key,
    256,
  );
  return toBase64(bits);
}

/** 長さの違いで漏れないよう、突き合わせは定数時間で行う */
function equalsConstantTime(a: string, b: string): boolean {
  if (a.length !== b.length) return false;
  let diff = 0;
  for (let i = 0; i < a.length; i++) diff |= a.charCodeAt(i) ^ b.charCodeAt(i);
  return diff === 0;
}

/**
 * アカウントを作る。呼び出し側で validateUserName / validatePassword を通してから使う
 * （ここでも念のため検証し、だめなら例外にする）。
 */
export async function createAccount(
  rawName: string,
  password: string,
  now: number,
  existing: UserAccount[] = [],
): Promise<UserAccount> {
  const name = validateUserName(rawName, existing);
  if (!name.ok) throw new Error(name.reason);
  const pw = validatePassword(password);
  if (!pw.ok) throw new Error(pw.reason);

  const salt = new Uint8Array(16);
  crypto.getRandomValues(salt);
  const hash = await derive(password, salt, PBKDF2_ITERATIONS);
  return {
    id: crypto.randomUUID(),
    name: name.value,
    nameKey: userNameKey(name.value),
    salt: toBase64(salt.buffer as ArrayBuffer),
    hash,
    iterations: PBKDF2_ITERATIONS,
    createdAt: now,
  };
}

export async function verifyPassword(account: UserAccount, password: string): Promise<boolean> {
  const hash = await derive(password, fromBase64(account.salt), account.iterations);
  return equalsConstantTime(hash, account.hash);
}

export function findAccountByName(users: UserAccount[], rawName: string): UserAccount | null {
  const key = userNameKey(rawName);
  return users.find((u) => u.nameKey === key) ?? null;
}

/** パスワードの変更。名前と作成日はそのまま引き継ぐ */
export async function changePassword(
  account: UserAccount,
  newPassword: string,
): Promise<UserAccount> {
  const pw = validatePassword(newPassword);
  if (!pw.ok) throw new Error(pw.reason);
  const salt = new Uint8Array(16);
  crypto.getRandomValues(salt);
  return {
    ...account,
    salt: toBase64(salt.buffer as ArrayBuffer),
    hash: await derive(newPassword, salt, PBKDF2_ITERATIONS),
    iterations: PBKDF2_ITERATIONS,
  };
}
